import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil, isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  DocumentSeriesDefinitionResponseBody,
  useDeleteDocumentSeriesDefinitionMutation,
  useGetDocumentSeriesDefinitionListQuery,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {
  DmsDataGrid,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {ApiError} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {InvoiceDefinitionForm} from './components/InvoiceDefinitionForm';

const INVOICE_DEFINITION_GRID_CODE = 'accounting-invoice-definition';

export function InvoiceDefinition() {
  const [deleteDefinition] = useDeleteDocumentSeriesDefinitionMutation();
  const {data: invoiceDefinitionItems, refetch: refetchDefinitions} =
    useGetDocumentSeriesDefinitionListQuery();

  const title = i18n.t('page.accounting.labels.invoiceDefinition');

  const openDefinitionDialog = (definition: DocumentSeriesDefinitionResponseBody | null) =>
    openDialog(
      <InvoiceDefinitionForm
        definition={definition}
        data-testid={testIds.settings.invoiceDefinition('InvoiceDefinitionForm')}
      />,
      {
        title,
        scrollBehavior: 'outside',
        onCloseComplete: () => refreshDmsDatagrid(INVOICE_DEFINITION_GRID_CODE),
      }
    );

  // The grid can show a row before the cached list refetches (e.g. right after create),
  // so fall back to a fresh list when the row is not cached yet.
  const findDefinition = (id: string | undefined) => {
    const cachedDefinition = invoiceDefinitionItems?.find((item) => item.id === id);

    if (isNotNil(cachedDefinition)) {
      return Promise.resolve(cachedDefinition);
    }

    return refetchDefinitions()
      .unwrap()
      .then((definitions) => definitions.find((item) => item.id === id))
      .catch((error: ApiError) => {
        handleApiError(error);
        return undefined;
      });
  };

  const actionCallback: ActionCallback = async ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;
    const definition = await findDefinition(id);

    if (isNil(definition)) {
      return logError(`Invoice definition ${id} for action ${actionKey} not found`);
    }

    match(actionKey)
      .with('DETAIL', 'EDIT', () => openDefinitionDialog(definition))
      .with('DELETE', () =>
        openDeleteDialog({
          text: i18n.t('entity.accounting.labels.deleteDefinitionConfirmationText', {
            name: definition.name ?? '',
          }),
          onConfirm: () =>
            deleteDefinition({documentSeriesDefinitionId: definition.id})
              .unwrap()
              .then(() => showNotification.success())
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.newInvoiceDefinition'),
        onClick: () => openDefinitionDialog(null),
        'data-testid': testIds.settings.invoiceDefinition('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate header={header} data-testid="settings-invoice-definition-modal">
        <DmsDataGrid
          gridCode={INVOICE_DEFINITION_GRID_CODE}
          actionCallback={actionCallback}
          data-testid={testIds.settings.invoiceDefinition('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
